import { ObjectSchema } from 'yup';

import { supportedOrganizationTypes } from '@sravni/ugc/lib/organizations/maps';

import { CUSTOMER_TYPES } from '../../constants/reviews';
import { ERROR_DICTIONARY, ERRORS, makeMaxLengthMessage, makeMinLengthMessage } from '../../utils/errorDictionary';
import yup from '../../utils/yup';

import { LENGTH_MAX, LENGTH_MIN, REVIEW_FIELDS } from './constants/common';
import { TFormInputs } from './types';

type TSchemaParams = {
  isUserLogged: boolean;
  isRequiredUserData: boolean;
  isReadOnly: boolean;
};

export const createSchema = ({
  isUserLogged,
  isRequiredUserData,
  isReadOnly,
}: TSchemaParams): ObjectSchema<TFormInputs> =>
  yup.object().shape({
    [REVIEW_FIELDS.RATING]: yup.number().required(ERROR_DICTIONARY[ERRORS.REQUIRED_RATING]),

    [REVIEW_FIELDS.TITLE]: yup
      .string()
      .required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD])
      .when([], {
        is: () => isReadOnly,
        otherwise: (schema) =>
          schema
            .repeatingChars()
            .sanitizeAndTypograf()
            .min(LENGTH_MIN[REVIEW_FIELDS.TITLE], makeMinLengthMessage(LENGTH_MIN[REVIEW_FIELDS.TITLE]))
            .max(LENGTH_MAX[REVIEW_FIELDS.TITLE], makeMaxLengthMessage(LENGTH_MAX[REVIEW_FIELDS.TITLE])),
      }),
    [REVIEW_FIELDS.TEXT]: yup
      .string()
      .required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD])
      .when([], {
        is: () => isReadOnly,
        otherwise: (schema) =>
          schema
            .repeatingChars()
            .sanitizeAndTypograf()
            .min(LENGTH_MIN[REVIEW_FIELDS.TEXT], ERROR_DICTIONARY[ERRORS.MIN_REVIEW_TEXT])
            .max(LENGTH_MAX[REVIEW_FIELDS.TEXT], ERROR_DICTIONARY[ERRORS.MAX_REVIEW_TEXT]),
      }),

    [REVIEW_FIELDS.ORGANIZATION_TYPE]: yup
      .string()
      .required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD])
      .oneOf(supportedOrganizationTypes),
    [REVIEW_FIELDS.ORGANIZATION_ID]: yup.string().required(ERROR_DICTIONARY[ERRORS.REQUIRED_ORGANIZATION]),
    [REVIEW_FIELDS.SERVICE]: yup.string().required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]),
    [REVIEW_FIELDS.PRODUCT_ID]: yup.string().notRequired(),
    [REVIEW_FIELDS.LOCATION_GAR_ID]: yup.number().required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]),
    [REVIEW_FIELDS.IS_LEGAL]: yup.string().oneOf([CUSTOMER_TYPES.Individuals, CUSTOMER_TYPES.Businesses]).nullable(),

    [REVIEW_FIELDS.USER_NAME]: yup
      .string()
      .trim()
      .name()
      .sanitizeAndTypograf()
      .max(LENGTH_MAX[REVIEW_FIELDS.USER_NAME], makeMaxLengthMessage[REVIEW_FIELDS.USER_NAME])
      .when([], {
        is: () => isRequiredUserData,
        then: (schema) => schema.required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]),
        otherwise: (schema) => schema.notRequired(),
      }),
    [REVIEW_FIELDS.USER_PHONE]: yup
      .string()
      .normalizePhone()
      .when([], {
        is: () => isRequiredUserData,
        then: (schema) => schema.required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]),
        otherwise: (schema) => schema.notRequired(),
      })
      .mobilePhone(ERROR_DICTIONARY[ERRORS.INVALID_PHONE]),
    [REVIEW_FIELDS.BIRTHDAY]: yup
      .string()
      .normalizeBirthday()
      .birthday()
      .when([], {
        is: () => isRequiredUserData,
        then: (schema) => schema.required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]),
        otherwise: (schema) => schema.notRequired(),
      }),
    [REVIEW_FIELDS.MESSAGE_FOR_COMPANY]: yup
      .string()
      .trim()
      .sanitizeAndTypograf()
      .max(
        LENGTH_MAX[REVIEW_FIELDS.MESSAGE_FOR_COMPANY],
        makeMaxLengthMessage(LENGTH_MAX[REVIEW_FIELDS.MESSAGE_FOR_COMPANY]),
      )
      .when([], {
        is: () => isRequiredUserData,
        then: (schema) => schema.required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]),
        otherwise: (schema) => schema.notRequired(),
      }),
    [REVIEW_FIELDS.ATTACHMENTS]: yup.array().of(yup.string()),

    [REVIEW_FIELDS.AGREEMENT]: isUserLogged
      ? yup.boolean().isTrue(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD]).required(ERROR_DICTIONARY[ERRORS.REQUIRED_FIELD])
      : yup.boolean().notRequired(),

    [REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT]: yup.boolean().isTrue(),
  });
